import clsx from 'clsx';
import React from 'react';
import { AiFillCheckCircle } from 'react-icons/ai';
import { FcProcess } from 'react-icons/fc';
import { MdCancel } from 'react-icons/md';
import { PiClockCountdownBold } from 'react-icons/pi';

import { Button } from '@/components/button/button';
import Modal from '@/components/modal/modal';
import { Heading } from '@/components/typography/heading';
import { ContractTaskStatus, TaskStatusResponse } from '@/types/dataContracts';
import { friendlyPulumiError } from '@/utils/friendlyPulumiError';

import styles from './page.module.scss';

interface ProcessingModalProps {
  taskStatus: TaskStatusResponse | undefined;
  isLoading: boolean;
  error: Error | null;
  onClose: () => void;
  onViewDetails?: () => void;
}

export default function ProcessingModal( { taskStatus, isLoading, error, onClose, onViewDetails }: ProcessingModalProps ) {
  const status = taskStatus?.status;
  const isTerminal = status === ContractTaskStatus.SUCCESS || status === ContractTaskStatus.FAILURE || status === ContractTaskStatus.REVOKED;

  const renderStatusIcon = () => {
    switch ( status ) {
      case ContractTaskStatus.PENDING:
        return <PiClockCountdownBold className={ styles.status_icon_queued } />;
      case ContractTaskStatus.STARTED:
      case ContractTaskStatus.ACCESSCONTROL:
        return <FcProcess className={ styles.status_icon_processing } />;
      case ContractTaskStatus.SUCCESS:
        return <AiFillCheckCircle className={ styles.status_icon_completed } />;
      case ContractTaskStatus.FAILURE:
      case ContractTaskStatus.REVOKED:
        return <MdCancel className={ styles.status_icon_failed } />;
      default:
        return <PiClockCountdownBold className={ styles.status_icon_queued } />;
    }
  };

  const renderStatusText = () => {
    if ( isLoading && !taskStatus ) return 'Submitting contract...';
    if ( error ) return 'Error checking status';

    switch ( status ) {
      case ContractTaskStatus.PENDING:
        return 'Contract queued for processing...';
      case ContractTaskStatus.STARTED:
      case ContractTaskStatus.ACCESSCONTROL:
        return taskStatus?.progress_message || 'Processing contract...';
      case ContractTaskStatus.SUCCESS:
        return 'Contract created successfully!';
      case ContractTaskStatus.FAILURE:
      case ContractTaskStatus.REVOKED:
        return 'Contract creation failed';
      case ContractTaskStatus.RETRY:
        return 'Retrying contract creation...';
      default:
        return 'Submitting contract...';
    }
  };

  const progress = taskStatus?.progress ?? 0;

  return (
    <Modal>
      <div className={ clsx( 'layout__col', 'pad', styles.processing_modal ) }>
        <Heading as="h2" size="md">Creating Contract</Heading>

        <div className={ styles.processing_status }>
          { renderStatusIcon() }
          <span>{ renderStatusText() }</span>
        </div>

        <div className={ styles.progress_bar_container }>
          <div
            className={ clsx(
              styles.progress_bar_fill,
              status === ContractTaskStatus.SUCCESS && styles.progress_bar_completed,
              ( status === ContractTaskStatus.FAILURE || status === ContractTaskStatus.REVOKED ) && styles.progress_bar_failed
            ) }
            style={ { width: `${ progress }%` } }
          />
        </div>
        <span className={ styles.progress_text }>{ progress }%</span>

        { ( status === ContractTaskStatus.FAILURE || status === ContractTaskStatus.REVOKED ) && taskStatus?.error && (
          <div className={ styles.error_message }>
            { ( () => {
              const friendly = friendlyPulumiError( taskStatus.error );
              if ( friendly ) {
                return (
                  <>
                    <div className={ styles.error_description }>{ friendly.description }</div>
                    <details className={ styles.error_details }>
                      <summary>Show full error log</summary>
                      <pre className={ styles.error_raw }>{ friendly.raw }</pre>
                    </details>
                  </>
                );
              }
              return <pre className={ styles.error_raw }>{ taskStatus.error }</pre>;
            } )() }
          </div>
        ) }

        <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
          { isTerminal && ( status === ContractTaskStatus.FAILURE || status === ContractTaskStatus.REVOKED ) && onViewDetails && (
            <Button variant='secondary' onClick={ onViewDetails }>
              View Details
            </Button>
          ) }
          <Button variant={ isTerminal ? 'primary' : 'secondary' } onClick={ onClose }>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
